import { Button, type ButtonProps, type ContainerProps, Flex } from '@chakra-ui/react';
import React from 'react';
import ConfirmPopup from '../ConfirmPopup';

const variantStyles = {
  default: {
    flexDir: { base: 'column', md: 'row' },
    gap: { base: 'xs', md: 'sm' },
    mt: '2xl',
  } satisfies ContainerProps,
  sticky: {
    position: 'sticky',
    bottom: 8,
    bgColor: 'white',
    w: 'max',
    p: 'lg',
    borderRadius: 'xl',
    borderWidth: '1px',
    borderColor: 'border.pure',
    ml: 'auto',
    gap: 'xs',
  } satisfies ContainerProps,
};

interface IActionButtonsProps extends ContainerProps {
  isMobile?: boolean;
  onCancel?: () => void;
  onDelete?: () => void;
  onSave?: () => void;
  showDelete?: boolean;
  isSubmitting?: boolean;
  isDeleting?: boolean;
  isDisabled?: boolean;
  deleteButtonText?: string;
  hideSubmit?: boolean;
  message?: string;
  positiveButtonProps?: ButtonProps;
  positiveButtonText?: string;
  PositiveButton?: React.FC<ButtonProps>;
  negativeButtonText?: string;
  negativeButtonProps?: ButtonProps;
  deleteButtonProps?: ButtonProps;
  showConfirmOnDelete?: boolean;
  variant?: 'default' | 'sticky';
  DeleteButton?: React.FC<ButtonProps>;
}

const DefaultDeleteButton: React.FC<ButtonProps> = ({ children, ...props }) => (
  <Button
    colorScheme='delete'
    minH='10'
    variant='link'
    {...props}
  >
    {children}
  </Button>
);

const ActionButtons: React.FC<IActionButtonsProps> = ({
  onCancel,
  onDelete,
  onSave,
  isMobile,
  showDelete,
  isSubmitting,
  isDeleting,
  isDisabled,
  deleteButtonText = 'Delete',
  positiveButtonText = 'Save',
  negativeButtonText = 'Cancel',
  hideSubmit,
  message,
  positiveButtonProps,
  negativeButtonProps,
  deleteButtonProps,
  PositiveButton = Button,
  DeleteButton = DefaultDeleteButton,
  showConfirmOnDelete = true,
  variant = 'default',
  ...props
}) => {
  const DeleteBtn = showConfirmOnDelete ? (
    <ConfirmPopup
      PreviewButton={(props) =>
        DeleteButton({ children: deleteButtonText, ...props, ...deleteButtonProps })
      }
      isMobile={isMobile}
      message={message}
      onConfirm={onDelete!}
    />
  ) : (
    <DeleteButton
      {...deleteButtonProps}
      onClick={onDelete}
    >
      {deleteButtonText}
    </DeleteButton>
  );

  return (
    <Flex
      {...variantStyles[variant]}
      {...props}
    >
      {showDelete && onDelete && DeleteBtn}
      {!!onCancel && (
        <Button
          colorScheme='primary'
          isDisabled={isSubmitting || isDeleting}
          variant='outline'
          onClick={onCancel}
          {...negativeButtonProps}
        >
          {negativeButtonText}
        </Button>
      )}
      {!hideSubmit && (
        <PositiveButton
          isDisabled={isDisabled || isDeleting}
          isLoading={isSubmitting}
          variant='primary'
          onClick={onSave}
          {...positiveButtonProps}
        >
          {positiveButtonText}
        </PositiveButton>
      )}
    </Flex>
  );
};

export default ActionButtons;
